import { setOpacity } from "@memorang/helpers/setOpacity";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Button, Divider, ProgressBar, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../../../useAppTheme";
import { useDevice } from "../../../../useDevice";
import { AppIcon } from "../../../AppIcon";
import Box from "../../../Box";
import TooltipWrapper from "../../../TooltipWrapper";
import TimerChip from "../TimerChip";

export type TimerScope = "item" | "section" | "test";

type Props = {
	testTitle: string;
	sectionLabel?: string;
	partProgressLabel?: string;
	currentItemIndex: number;
	numCurrentSessionItems: number;
	currentProgress?: number;
	blocks?: { currentProgress?: number }[];
	currentBlockIndex?: number;
	numTotalBlocks?: number;
	sessionTime?: string;
	timerScope?: TimerScope;
	isTimerExpired?: boolean;
	showSectionProgress?: boolean;
	showItemCount?: boolean;
	showSectionLabel?: boolean;
	onBackPress?: () => void;
	onPauseSession?: () => void;
};

const SectionProgressBar = ({
	segments,
}: {
	segments: { currentProgress?: number }[];
}) => {
	const { t } = useTranslation();
	const theme = useAppTheme();

	return (
		<Box flexDirection="row" alignItems="center" gap={8} flex={1}>
			{segments.map(({ currentProgress }, index) => (
				<Box key={index.toString()} flex={1}>
					<ProgressBar
						aria-label={t("testDriver.pteSummativeAppbar.progressBarLabel")}
						progress={currentProgress || 0}
						style={{
							height: 6,
							backgroundColor: setOpacity(theme.colors.primary, 0.3),
						}}
					/>
				</Box>
			))}
		</Box>
	);
};

type AppbarRightProps = {
	currentItemIndex: number;
	numCurrentSessionItems: number;
	sectionProgressLabel?: string;
	sessionTime?: string;
	timerTooltip?: string;
	showItemCount?: boolean;
	onPauseSession?: () => void;
};

const AppbarRight = ({
	currentItemIndex,
	numCurrentSessionItems,
	sectionProgressLabel,
	sessionTime,
	timerTooltip,
	showItemCount = true,
	onPauseSession,
}: AppbarRightProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	return (
		<Box flexDirection="row" alignItems="center" gap={16}>
			{showItemCount ? (
				<Box alignItems="flex-end" gap={2}>
					{sectionProgressLabel ? (
						<Text
							variant="bodyMedium"
							style={{ color: theme.palette.text.secondary }}
						>
							{sectionProgressLabel}
						</Text>
					) : null}
					<Box flexDirection="row" alignItems="center" gap={4}>
						<Text
							variant="bodyLarge"
							style={{ fontWeight: "700", color: theme.palette.text.primary }}
						>
							{currentItemIndex}
						</Text>
						<Text
							variant="bodyLarge"
							style={{ color: theme.palette.text.secondary }}
						>
							{t("testDriver.pteSummativeAppbar.of")}
						</Text>
						<Text
							variant="bodyLarge"
							style={{ color: theme.palette.text.secondary }}
						>
							{numCurrentSessionItems}
						</Text>
					</Box>
				</Box>
			) : null}
			{sessionTime ? (
				<>
					{showItemCount ? (
						<Divider
							style={{ height: sectionProgressLabel ? 40 : 24, width: 1 }}
						/>
					) : null}
					{timerTooltip ? (
						<TooltipWrapper title={timerTooltip}>
							<TimerChip label={sessionTime} />
						</TooltipWrapper>
					) : (
						<TimerChip label={sessionTime} />
					)}
				</>
			) : null}
			{onPauseSession ? (
				<Button mode="outlined" onPress={onPauseSession} compact>
					{t("testDriver.pteSummativeAppbar.pauseSession")}
				</Button>
			) : null}
		</Box>
	);
};

const PteSummativeAppbar = ({
	testTitle,
	sectionLabel,
	partProgressLabel,
	currentItemIndex,
	numCurrentSessionItems,
	currentProgress = 0,
	blocks = [],
	currentBlockIndex = 0,
	numTotalBlocks = 1,
	sessionTime,
	timerScope,
	isTimerExpired,
	showSectionProgress = true,
	showItemCount = true,
	showSectionLabel = true,
	onBackPress,
	onPauseSession,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const insets = useSafeAreaInsets();

	const displayedSessionTime =
		sessionTime && isTimerExpired
			? t("testDriver.progressBar.timesUp")
			: sessionTime;

	const timerTooltip = timerScope
		? {
				item: t("testDriver.pteSummativeAppbar.timerTooltip.item"),
				section: t("testDriver.pteSummativeAppbar.timerTooltip.section"),
				test: t("testDriver.pteSummativeAppbar.timerTooltip.test"),
			}[timerScope]
		: undefined;

	const segments = blocks.length > 0 ? blocks : [{ currentProgress }];
	const sectionProgressLabel =
		showSectionLabel && numTotalBlocks > 1
			? `Section ${currentBlockIndex + 1} of ${numTotalBlocks}`
			: undefined;

	if (isMobile) {
		return (
			<Box
				backgroundColor={theme.colors.onPrimary}
				borderBottomWidth={1}
				borderBottomColor={theme.colors.surfaceVariant}
				paddingTop={insets.top}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
					gap={12}
					paddingHorizontal={16}
					paddingTop={16}
					paddingBottom={12}
				>
					<Text
						numberOfLines={1}
						style={{
							flex: 1,
							fontSize: 18,
							lineHeight: 22,
							fontWeight: "700",
							color: theme.palette.text.primary,
						}}
					>
						{testTitle}
					</Text>
					{sectionLabel ? (
						<Text
							numberOfLines={1}
							variant="bodyLarge"
							style={{
								lineHeight: 22,
								color: theme.palette.text.secondary,
							}}
						>
							{sectionLabel}
						</Text>
					) : null}
				</Box>
				<Box
					flexDirection="row"
					alignItems="center"
					gap={8}
					paddingRight={16}
					paddingLeft={10}
					paddingBottom={12}
				>
					{onBackPress ? (
						<Pressable
							onPress={onBackPress}
							accessibilityRole="button"
							accessibilityLabel={t("testDriver.appBar.back")}
						>
							<AppIcon
								name="arrow-left"
								size={24}
								color={theme.palette.text.primary}
							/>
						</Pressable>
					) : null}
					{showSectionProgress ? (
						<SectionProgressBar segments={segments} />
					) : (
						<Box flex={1} />
					)}
					{displayedSessionTime ? (
						<Box minWidth={50} alignItems="flex-end">
							<Text
								variant="bodyLarge"
								style={{
									textAlign: "right",
									lineHeight: 22,
									fontWeight: "700",
									color: theme.palette.text.primary,
								}}
							>
								{displayedSessionTime}
							</Text>
						</Box>
					) : null}
				</Box>
			</Box>
		);
	}

	return (
		<Box backgroundColor={theme.colors.onPrimary} paddingBottom={2}>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
				gap={16}
				paddingHorizontal={24}
				paddingVertical={16}
			>
				<Box flex={1} gap={4}>
					<Text
						style={{
							fontSize: 18,
							lineHeight: 22,
							fontWeight: "700",
							color: theme.palette.text.primary,
						}}
					>
						{testTitle}
					</Text>
					<Box flexDirection="row" alignItems="center" gap={8}>
						{partProgressLabel ? (
							<Text
								variant="bodyMedium"
								style={{
									fontWeight: "700",
									color: theme.palette.text.primary,
								}}
							>
								{partProgressLabel}
							</Text>
						) : null}
						{sectionLabel ? (
							<Text
								variant="bodyMedium"
								style={{ color: theme.palette.text.primary }}
							>
								{sectionLabel}
							</Text>
						) : null}
					</Box>
				</Box>
				<AppbarRight
					currentItemIndex={currentItemIndex}
					numCurrentSessionItems={numCurrentSessionItems}
					sectionProgressLabel={sectionProgressLabel}
					sessionTime={displayedSessionTime}
					timerTooltip={timerTooltip}
					showItemCount={showItemCount}
					onPauseSession={onPauseSession}
				/>
			</Box>
			{showSectionProgress ? <SectionProgressBar segments={segments} /> : null}
		</Box>
	);
};

export default PteSummativeAppbar;
